<template>
  <article
    :id="`pack-card-${iconPack.name}`"
    :class="['pack-card card with-shadow', { 'margin-bottom-0': isLast }]"
    style="--card-margin-bottom: var(--spacing-2xl); --card-section-border: var(--border-width-sm) solid var(--fa-lt-gravy)"
  >
    <div class="section compact">
      <div class="row align-middle">
        <div
          class="column-9 tablet:column display-flex flex-items-center"
          style="gap: var(--spacing-md)"
        >
          <h3
            class="size-lg text-uppercase margin-0"
            style="letter-spacing: var(--letter-spacing-2xl); --headings-font-weight: var(--font-weight-black)"
          >
            {{ iconPack.label }}
          </h3>

          <wa-tag
            if="uxIsNew"
            background="var(--fa-navy)"
            color="var(--fa-blue)"
            is-rounded
            label="New"
          />

          <p class="size-sm muted"><span class="sr-only">This pack contains: </span> {{ formattedCount(totalCount(familyStyles)) }} Icons</p>
        </div>
        <div
          v-if="isShowingIconPackViewLinks"
          class="column-3 tablet:column-3 text-right"
        >
          <site-link
            :to="$pack({ name: iconPack.name })"
            class="size-sm button compact flat with-border margin-bottom-0"
            style="--button-font-weight: var(--font-weight-normal)"
            :aria-label="`Explore ${iconPack.label}`"
            data-balloon-pos="left"
            @click.native="$posthog.capture('icons_landing:pack_link_click', { pack: iconPack.name })"
          >
            <span class="display-none laptop:display-inline laptop:margin-right-4xs">Explore {{ iconPack.label }}</span>
            <font-awesome-icon
              :icon="$v6.solid.faArrowUpRight"
              size="lg"
            />
          </site-link>
        </div>
      </div>
    </div>

    <site-link
      v-for="(familyStyle, index) in familyStyles"
      :key="index"
      :to="$search({ iconPacks: [iconPack.name], styles: [familyStyle.style] })"
      class="section no-underline pack-card-style position-relative display-flex flex-items-center"
      style="gap: var(--spacing-2xl)"
      @click.native="$posthog.capture('icons_landing:pack_style_link_click', { pack: iconPack.name, style: `${familyStyle.family} ${familyStyle.style}` })"
    >
      <div class="pack-card-style-details padding-right-5xl z-1">
        <div
          class="size-sm display-flex flex-items-center flex-content-center"
          style="gap: var(--spacing-lg); height: 100%"
        >
          <h4
            class="h5 text-uppercase margin-0 text-nowrap"
            style="color: var(--fa-navy); letter-spacing: var(--letter-spacing-2xl); --headings-font-weight: var(--font-weight-black)"
          >
            {{ familyStyle.label }}
          </h4>
          <p class="text-nowrap">
            <span class="sr-only">This style contains: </span>
            {{ formattedCount(familyStyleCount(familyStyle)) }} Icons
          </p>
        </div>
      </div>

      <i
        v-for="(icon, i) in featuredIcons"
        :key="i"
        :class="iconClass({ ...familyStyle, icon: icon })"
      >
      </i>
    </site-link>
  </article>
</template>

<script>
  import { find } from 'lodash'
  import UtilsMixin from 'app/views/UtilsMixin'
  import { FAStylesByShorthand } from '@fortawesome-internal-tools/fontawesome-icons/canonical' // eslint-disable-line import/no-unresolved
  import { FeaturedIconsMixin } from 'app/views/icons/mixins.js'
  import { IconPackMixin } from 'app/views/icons/packs/mixins.js'

  export default {
    name: 'PackCard',

    mixins: [FeaturedIconsMixin, IconPackMixin, UtilsMixin],

    props: {
      iconPack: {
        type: Object,
        required: true
      },
      familyStyles: {
        type: Array,
        required: true
      },
      isLast: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        iconSetSize: 16
      }
    },

    computed: {
      featuredIcons() {
        return this.getFeaturedIcons(this.iconPack.name, this.iconSetSize)
      },

      isShowingIconPackViewLinks() {
        return this.isIconPackPagesFeatureOn && this.$releases.active.versionParts.major !== 5
      }
    },

    methods: {
      iconClass({ family, style, icon }) {
        const canonical = find(FAStylesByShorthand, { familyId: family, styleId: style })
        const legacy = FAStylesByShorthand[style]

        const shortPrefixId = (canonical || legacy).shortPrefixId

        return `icon ${shortPrefixId} fa-${icon} fa-fw fa-2x`
      },

      totalCount(familyStyles) {
        return familyStyles.reduce((acc, familyStyle) => {
          return acc + this.familyStyleCount(familyStyle)
        }, 0)
      },

      formattedCount(totalCount) {
        const precision = 0

        return this.formatNumber(totalCount, { precision: precision })
      },

      familyStyleCount({ family, style }) {
        return this.$releases.active.familyStyleCounts[family][style].pro
      }
    }
  }
</script>
